import React, { useState } from 'react';
import {
    Button,
    TextField,
    Typography,
    Box,
    Alert,
    CircularProgress,
    DialogContent,
    DialogActions
} from '@mui/material';
import { CloudUpload as UploadIcon, Check as CheckIcon } from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const BulkUrlImportDialog = ({ open, onClose, onImport }) => {
    const [urlsText, setUrlsText] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleImport = async () => {
        if (!urlsText.trim()) return;

        setLoading(true);
        setError('');
        setResult(null);

        const urls = urlsText.split('\n').map(l => l.trim()).filter(Boolean);

        if (urls.length === 0) {
            setError('Nenhuma URL válida encontrada');
            setLoading(false);
            return;
        }

        try {
            const response = await onImport(urls);
            if (response.success) {
                setResult(response.data);
                setUrlsText('');
            } else {
                setError(response.message || 'Erro ao importar');
            }
        } catch (err) {
            setError('Falha na comunicação com servidor');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setResult(null);
            setError('');
            setUrlsText('');
            onClose();
        }
    };

    return (
        <PlatformDialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <PlatformDialogTitle
                title="Importação em Massa"
                subtitle="Adicione múltiplos domínios colando URLs abaixo."
                icon={<UploadIcon />}
                onClose={handleClose}
            />
            <DialogContent sx={{ pt: 3, pb: 2 }}>
                {!result ? (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Cole uma lista de links (um por linha). O sistema extrairá os domínios únicos e criará os registros pendentes.
                        </Typography>
                        <TextField
                            multiline
                            rows={8}
                            fullWidth
                            placeholder="https://exemplo.com/noticia/123&#10;www.outro-site.com.br&#10;blog.teste.com"
                            value={urlsText}
                            onChange={(e) => setUrlsText(e.target.value)}
                            variant="outlined"
                            disabled={loading}
                            sx={{ 
                                mb: 2,
                                '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'action.hover' }
                            }}
                        />
                    </>
                ) : (
                    <Box sx={{ py: 1 }}>
                        <Alert severity="success" variant="filled" sx={{ mb: 3, borderRadius: 2 }}>
                            Importação concluída com sucesso!
                        </Alert>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Box textAlign="center" p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2, flex: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="h4" color="success.main" fontWeight={700}>{result.created}</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">Novos Criados</Typography>
                            </Box>
                            <Box textAlign="center" p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2, flex: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="h4" color="info.main" fontWeight={700}>{result.updated}</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">Atualizados</Typography>
                            </Box>
                            <Box textAlign="center" p={2} sx={{ bgcolor: 'action.hover', borderRadius: 2, flex: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="h4" color="text.secondary" fontWeight={700}>{result.processed}</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">Total Processados</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
                {!result ? (
                    <>
                        <Button onClick={handleClose} disabled={loading} sx={{ fontWeight: 600 }}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleImport}
                            variant="contained"
                            disabled={loading || !urlsText.trim()}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                            sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}
                        >
                            {loading ? 'Processando...' : 'Iniciar Importação'}
                        </Button>
                    </>
                ) : (
                    <Button 
                        onClick={handleClose} 
                        variant="contained"
                        fullWidth
                        sx={{ fontWeight: 700, borderRadius: 2 }}
                        startIcon={<CheckIcon />}
                    >
                        Concluído
                    </Button>
                )}
            </DialogActions>
        </PlatformDialog>
    );
};

export default BulkUrlImportDialog;
